import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import baseUrl from "../../api/utils";
import BulkUpdateRow from "./BulkUpdateRow";
import "../../assets/css/invoicestyle.css";

const MySwal = withReactContent(Swal);

const TABS = [
  { key: "Pricing", label: "Pricing" },
  { key: "Stock", label: "Stock" },
  { key: "ItemInfo", label: "Item Information" },
];

const BulkUpdateItem = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [activeTab, setActiveTab] = useState("Pricing");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTaxMenu, setShowTaxMenu] = useState(false);

  // dirty maps: itemId -> full row object (as currently edited)
  const [dirtyPricing, setDirtyPricing] = useState({});
  const [dirtyStock, setDirtyStock] = useState({});
  const [dirtyItemInfo, setDirtyItemInfo] = useState({});

  const [resultErrors, setResultErrors] = useState([]);

  // ── Load everything on mount ──
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [itemsRes, catRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${baseUrl}/bulkUpdateItemsList`, { headers: { Authorization: token } }),
          axios.get(`${baseUrl}/getCategories`, { headers: { Authorization: token } }),
          axios.get(`${baseUrl}/getUnits`, { headers: { Authorization: token } }),
          axios.get(`${baseUrl}/getTaxes`, { headers: { Authorization: token } }),
        ]);
        setItems(itemsRes.data || []);
        setCategories(catRes.data || []);
        setUnits(unitRes.data || []);
        setTaxes(taxRes.data || []);
      } catch (err) {
        console.error("Failed to load bulk update data", err);
        MySwal.fire({ icon: "error", title: "Load Failed", text: "Could not load items." });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Search filter ──
  const filteredItems = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        (it.itemName || "").toLowerCase().includes(q) ||
        (it.itemHsn || "").toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const allChecked = filteredItems.length > 0 && filteredItems.every((it) => selectedIds.has(it.itemId));

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map((it) => it.itemId)));
    }
  };

  const toggleOne = (itemId) => {
    const next = new Set(selectedIds);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    setSelectedIds(next);
  };

  // ── Generic cell-change handler per section ──
  const updatePricingField = (item, field, value) => {
    setDirtyPricing((prev) => {
      const current = prev[item.itemId] || {
        itemId: item.itemId,
        itemHsn: item.itemHsn,
        categoryId: item.categoryId,
        purchasePrice: item.purchasePrice,
        purchasePriceTaxType: item.purchasePriceTaxType,
        salePrice: item.salePrice,
        salePriceTaxType: item.salePriceTaxType,
        wholesalePrice: item.wholesalePrice,
        wholesalePriceTaxType: item.wholesalePriceTaxType,
        discountOnSale: item.discountOnSale,
        discountType: item.discountType,
        taxId: item.taxId,
        mrp: item.mrp,
        calculateTaxOnMrp: item.calculateTaxOnMrp ? "Yes" : "No",
        additionalCessPerUnit: item.additionalCessPerUnit,
      };
      return { ...prev, [item.itemId]: { ...current, [field]: value } };
    });

    // reflect into the visible grid immediately
    setItems((prev) =>
      prev.map((it) => (it.itemId === item.itemId ? { ...it, [field]: value } : it))
    );
  };

  const updateStockField = (item, field, value) => {
    setDirtyStock((prev) => {
      const current = prev[item.itemId] || {
        itemId: item.itemId,
        openingStock: item.openingStock,
        stockAtPrice: item.stockAtPrice,
        stockAsOfDate: item.stockAsOfDate,
        minStockQty: item.minStockQty,
        location: item.location,
      };
      return { ...prev, [item.itemId]: { ...current, [field]: value } };
    });

    setItems((prev) =>
      prev.map((it) => (it.itemId === item.itemId ? { ...it, [field]: value } : it))
    );
  };

  const updateItemInfoField = (item, field, value) => {
    setDirtyItemInfo((prev) => {
      const current = prev[item.itemId] || {
        itemId: item.itemId,
        itemName: item.itemName,
        itemHsn: item.itemHsn,
        categoryId: item.categoryId,
        unitId: item.unitId,
      };
      return { ...prev, [item.itemId]: { ...current, [field]: value } };
    });

    setItems((prev) =>
      prev.map((it) => (it.itemId === item.itemId ? { ...it, [field]: value } : it))
    );
  };

  // ── Mass action: apply a tax to all selected rows (staged, not saved yet) ──
  const applyMassTax = (taxId) => {
    const tax = taxes.find((t) => t.id === taxId);
    filteredItems.forEach((item) => {
      if (selectedIds.has(item.itemId)) {
        updatePricingField(
          { ...item, taxId, taxName: tax?.name },
          "taxId",
          taxId
        );
        updatePricingField({ ...item, taxId }, "taxName", tax?.name);
      }
    });
    setShowTaxMenu(false);
  };

  const pricingCount = Object.keys(dirtyPricing).length;
  const stockCount = Object.keys(dirtyStock).length;
  const itemInfoCount = Object.keys(dirtyItemInfo).length;
  const totalDirty = pricingCount + stockCount + itemInfoCount;

  // ── Save ──
  const handleUpdate = async () => {
    if (totalDirty === 0) {
      MySwal.fire({ icon: "info", title: "No Changes", text: "You haven't changed anything yet." });
      return;
    }

    const payload = {
      pricingUpdates: Object.values(dirtyPricing),
      stockUpdates: Object.values(dirtyStock),
      itemInfoUpdates: Object.values(dirtyItemInfo),
    };

    try {
      setSaving(true);
      const res = await axios.post(`${baseUrl}/bulkUpdateItems`, payload, {
        headers: { Authorization: token, "Content-Type": "application/json" },
      });

      const result = res.data;
      setResultErrors(result.errors || []);

      if ((result.errors || []).length === 0) {
        MySwal.fire({
          icon: "success",
          title: "Items Updated",
          text: `Pricing: ${result.pricingUpdated}, Stock: ${result.stockUpdated}, Item Info: ${result.itemInfoUpdated} updated.`,
        });
      } else {
        MySwal.fire({
          icon: "warning",
          title: "Some Rows Failed",
          text: `${result.pricingUpdated + result.stockUpdated + result.itemInfoUpdated} updated. See error list below for skipped rows.`,
        });
      }

      // clear dirty state — trust local edits are now saved for the ones that succeeded
      setDirtyPricing({});
      setDirtyStock({});
      setDirtyItemInfo({});
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate("/unauthorized");
        return;
      }
      MySwal.fire({ icon: "error", title: "Update Failed", text: "Something went wrong. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const displayVal = (v) => (v === null || v === undefined || v === "" ? "---" : v);

  return (
    <div>
      <div className="invoice-card-wrapper vt-card-wrapper">
        <div>
          <div className="card invoice-card">
            <div className="card-body p-0">

              {/* ══ HEADER ══ */}
              <div className="invoice-right-panel-header">
                <div className="vt-right-header-left">
                    
                  <div onClick={() => navigate(-1)} style={{ cursor: "pointer", fontSize: "16px", color: "#333" }}>
                  <i className="fa-solid fa-arrow-left"></i>
                </div>  
                  <h6 className="invoice-right-panel-title">Bulk Update Items</h6>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="invoice-search-box" style={{ minWidth: 240 }}>
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend">
                        <span className="input-group-text bg-white border-right-0">
                          <i className="fa-solid fa-magnifying-glass" style={{ color: "#aaa" }}></i>
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control border-left-0"
                        placeholder="Search by item name / HSN Code"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ boxShadow: "none" }}
                      />
                    </div>
                  </div>

                  {TABS.map((tab) => (
                    <label key={tab.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                      <input
                        type="radio"
                        checked={activeTab === tab.key}
                        onChange={() => setActiveTab(tab.key)}
                      />
                      {tab.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* ══ SELECTION BAR (Pricing tab only, for mass tax action) ══ */}
              {activeTab === "Pricing" && selectedIds.size > 0 && (
                <div style={{ background: "#eaf3ff", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13 }}>{selectedIds.size} items selected</span>
                  <div style={{ position: "relative" }}>
                    <button className="btn btn-danger btn-sm" onClick={() => setShowTaxMenu((s) => !s)}>
                      Update Tax Slab <i className="fa-solid fa-chevron-down ml-1"></i>
                    </button>
                    {showTaxMenu && (
                      <div
                        style={{
                          position: "absolute", right: 0, top: "110%", background: "#fff",
                          border: "1px solid #ddd", borderRadius: 6, zIndex: 50, minWidth: 160,
                          maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      >
                        {taxes.map((tax) => (
                          <div
                            key={tax.id}
                            onClick={() => applyMassTax(tax.id)}
                            style={{ padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                          >
                            {tax.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ══ TABLE ══ */}
              <div style={{ overflowX: "auto", maxHeight: 520, overflowY: "auto" }}>
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border spinner-border-sm text-danger"></div>
                  </div>
                ) : (
                  <table className="table table-hover table-bordered table-sm invoice-table">
                    <thead style={{ background: "#222", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: 30 }}>
                          <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                        </th>
                        <th style={{ width: 30 }}>#</th>
                        <th>Item Name*</th>

                        {activeTab === "Pricing" && (
                          <>
                            <th>Category</th>
                            <th>Item HSN</th>
                            <th>Purchase Price</th>
                            <th>Tax Type</th>
                            <th>Sale Price</th>
                            <th>Tax Type</th>
                            <th>Discount</th>
                            <th>Discount Type</th>
                            <th>Tax</th>
                          </>
                        )}

                        {activeTab === "Stock" && (
                          <>
                            <th>Opening Quantity</th>
                            <th>At Price</th>
                            <th>As Of Date</th>
                            <th>Min. Stock to Maintain</th>
                            <th>Location</th>
                          </>
                        )}

                        {activeTab === "ItemInfo" && (
                          <>
                            <th>Item HSN</th>
                            <th>Category</th>
                            <th>Unit</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.length === 0 && (
                        <tr>
                          <td colSpan={12} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                            No items found
                          </td>
                        </tr>
                      )}

                       {filteredItems.map((item, idx) => (
                  <BulkUpdateRow
                    key={item.itemId}
                    item={item}
                    idx={idx}
                    activeTab={activeTab}
                    selectedIds={selectedIds}
                    categories={categories}
                    units={units}
                    taxes={taxes}
                    toggleOne={toggleOne}
                    updatePricingField={updatePricingField}
                    updateStockField={updateStockField}
                    updateItemInfoField={updateItemInfoField}
                  />
                ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* ══ ERROR LIST (after Update attempt) ══ */}
              {resultErrors.length > 0 && (
                <div style={{ padding: "12px 20px", background: "#fff0f0", borderTop: "1px solid #f5c6c6" }}>
                  <div style={{ color: "#d93025", fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                    <i className="fa-solid fa-circle-exclamation mr-2"></i>
                    Some rows were skipped:
                  </div>
                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {resultErrors.map((err, i) => (
                      <li key={i} className="text-danger" style={{ fontSize: "0.82rem" }}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ══ FOOTER ══ */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: "1px solid #eee" }}>
                <span style={{ fontSize: 13, color: "#555" }}>
                  Pricing - {pricingCount} Updates, Stock - {stockCount} Updates, Item Information - {itemInfoCount} Updates
                </span>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={saving || totalDirty === 0}
                  onClick={handleUpdate}
                >
                  {saving ? "Updating..." : "Update"}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateItem;